import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Typography, Box, Card, CardContent, Grid, TextField, InputAdornment,
  Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Avatar, Menu, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, Fab,
  Tabs, Tab, LinearProgress, Tooltip, CircularProgress, DialogContentText,
  Autocomplete, Grow, useTheme, useMediaQuery, Stack, Badge, Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  RocketLaunch,
  AutoAwesome,
  ArrowForward,
  Diamond,
  GetApp as ExportIcon,
  FilterAlt as FilterAltIcon,
  Edit as EditIcon,
  ViewList as TableViewIcon,
  ViewModule as CardViewIcon
} from '@mui/icons-material';
import FilterBar from '../../components/common/FilterBar';
import { useDocumentsData, useClientsData, useSuppliersData, deleteResource, updateResource } from '../../services/dataService';

// Global document reference to avoid confusion with document parameter
const globalDocument = window.document;

// FloatingActionCard component from Dashboard
const FloatingActionCard = ({ title, icon, color, action, description, badge, onClick }) => {
  // Get theme and check if mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Card sx={{
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
      border: `1px solid ${color}30`,
      borderRadius: { xs: 3, md: 4 },
      overflow: 'hidden',
      position: 'relative',
      cursor: 'pointer',
      height: '100%', // Ensure consistent height
    }} onClick={onClick}>
      <CardContent sx={{ 
        p: { xs: 2, sm: 2.5, md: 3 }, 
        position: 'relative', 
        zIndex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, md: 2 }, 
          mb: { xs: 1.5, md: 2 },
          flexWrap: 'wrap'
        }}>
          <Avatar className="action-icon" sx={{ 
            background: `linear-gradient(135deg, ${color}, ${color}cc)`,
            width: { xs: 48, sm: 52, md: 56 },
            height: { xs: 48, sm: 52, md: 56 },
            boxShadow: `0 8px 25px ${color}40`,
            '& .MuiSvgIcon-root': {
              fontSize: { xs: 26, sm: 28, md: 30 }
            }
          }}>
            {icon}
          </Avatar>
          {badge && (
            <Chip 
              label={badge} 
              size="small" 
              sx={{ 
                background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                color: 'white',
                fontWeight: 600,
                height: { xs: '20px', md: '24px' },
                fontSize: { xs: '0.7rem', md: '0.75rem' }
              }}
            />
          )}
        </Box>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700, 
            mb: { xs: 0.5, md: 1 }, 
            color: 'rgba(51,65,85,0.9)',
            lineHeight: 1.3,
            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'rgba(51,65,85,0.7)',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            display: { xs: '-webkit-box', md: 'block' },
            WebkitLineClamp: { xs: 2, md: 'none' },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

// EnhancedStatCard from Dashboard
const EnhancedStatCard = ({ title, value, icon, gradient, delay, onClick, subtitle, chart, trend, progress }) => (
  <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={1000}>
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        background: gradient ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` : 'white',
        color: gradient ? 'white' : 'inherit',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'transform 0.3s, box-shadow 0.3s',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          transform: 'translateY(-5px)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        } : {},
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ 
              opacity: gradient ? 0.9 : 0.7, 
              mb: 0.5, 
              fontWeight: 500,
              fontSize: { xs: '0.8rem', md: '0.875rem' }
            }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' }
            }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ 
                opacity: gradient ? 0.9 : 0.7, 
                mt: 0.5,
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}>
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Chip 
                label={trend} 
                size="small" 
                sx={{ 
                  mt: 1,
                  backgroundColor: trend.startsWith('+') ? 
                    (gradient ? 'rgba(255,255,255,0.3)' : '#059669') : 
                    (gradient ? 'rgba(255,255,255,0.3)' : '#dc2626'),
                  color: gradient ? 'white' : 'white',
                  fontWeight: 600,
                  fontSize: { xs: '0.65rem', md: '0.75rem' },
                  height: { xs: 20, md: 24 }
                }}
              />
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: gradient ? 'rgba(255,255,255,0.2)' : 'primary.light',
              color: gradient ? 'white' : 'primary.main',
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 }
            }}
          >
            {icon}
          </Avatar>
        </Box>
        
        {progress && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                Progression
              </Typography>
              <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: gradient ? 'white' : 'primary.main',
                }
              }} 
            />
          </Box>
        )}
        
        {chart && (
          <Box sx={{ mt: 2, height: { xs: 40, md: 60 } }}>
            {chart}
          </Box>
        )}
      </CardContent>
    </Card>
  </Grow>
);

const Documents = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('tous');
  const [selectedType, setSelectedType] = useState('tous');
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState('table');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const showQuickActions = false;
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [newDocument, setNewDocument] = useState({
    nom: '',
    type: '',
    categorie: '',
    description: '',
    proprietaire: '',
    dateCreation: new Date().toISOString().split('T')[0],
    statut: 'actif'
  });
  const [previewDialog, setPreviewDialog] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const fileInputRef = useRef(null);

  // PDF Export Function
  const exportDocumentsToPDF = () => {
    try {
      const { jsPDF } = require('jspdf');
      require('jspdf-autotable');
      
      const doc = new jsPDF();
      
      // Add header with company info
      doc.setFontSize(20);
      doc.setTextColor(100, 58, 237); // Purple color
      doc.text('TravelIT', 14, 22);
      
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text('Catalogue des Documents', 14, 30);
      
      // Add date
      doc.setFontSize(10);
      doc.setTextColor(150);
      const today = new Date().toLocaleDateString('fr-FR');
      doc.text(`Exporté le ${today}`, 14, 36);
      
      // Table header and data
      const tableColumn = ["Document", "Type", "Catégorie", "Propriétaire", "Date", "Statut"];
      const tableData = filteredDocuments.map(doc => [
        doc.nom,
        doc.type,
        doc.categorie,
        doc.proprietaire,
        new Date(doc.dateCreation).toLocaleDateString('fr-FR'),
        doc.statut
      ]);
      
      // Create the table
      doc.autoTable({
        head: [tableColumn],
        body: tableData,
        startY: 45,
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { 
          fillColor: [100, 58, 237],
          textColor: [255, 255, 255],
          fontStyle: 'bold' 
        },
        alternateRowStyles: { fillColor: [240, 240, 250] }
      });
      
      // Add summary information
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Total des documents: ${filteredDocuments.length}`, 14, finalY);
      
      // Save the PDF
      doc.save('catalogue-documents.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Une erreur est survenue lors de la génération du PDF.');
    }
  };

  // Fetch documents data using our hook
  const { documents, loading, error, stats, uploadDocument } = useDocumentsData({
    category: selectedCategory !== 'tous' ? selectedCategory : undefined,
    type: selectedType !== 'tous' ? selectedType : undefined,
    search: searchTerm
  });
  
  // Debug - log documents 
  useEffect(() => {
    console.log("Documents data received:", documents);
  }, [documents]);

  // Fetch clients and suppliers data for proprietaire dropdowns
  const { clients, loading: clientsLoading } = useClientsData();
  const { suppliers, loading: suppliersLoading } = useSuppliersData({ skipFinancial: true });



  // Filter documents based on search term, category, and type
  const filteredDocuments = Array.isArray(documents) ? documents.filter(doc => {
    const matchesSearch = searchTerm === '' || 
                         doc.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.proprietaire.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchTerm.toLowerCase());
                       
    const matchesCategory = selectedCategory === 'tous' || doc.categorie === selectedCategory;
    const matchesType = selectedType === 'tous' || doc.type === selectedType;
    
    return matchesSearch && matchesCategory && matchesType;
  }) : [];

  // Statistiques des documents - calculated from real data
  const statsCards = [
    {
      title: 'Total Documents',
      value: Array.isArray(documents) ? documents.length : 0,
      icon: <DescriptionIcon />,
      color: '#1e40af',
      change: '+0%'
    },
    {
      title: 'Documents Clients',
      value: Array.isArray(documents) ? documents.filter(doc => doc.categorie === 'clients').length : 0,
      icon: <AssignmentIcon />,
      color: '#7c3aed',
      change: '+0%'
    },
    {
      title: 'Contrats Actifs',
      value: Array.isArray(documents) ? documents.filter(doc => doc.type === 'contrat' && doc.statut === 'actif').length : 0,
      icon: <BusinessIcon />,
      color: '#059669',
      change: '+0%'
    },
    {
      title: 'En Attente',
      value: Array.isArray(documents) ? documents.filter(doc => doc.statut === 'en_attente').length : 0,
      icon: <FilterListIcon />,
      color: '#dc2626',
      change: '+0%'
    }
  ];

  // Catégories de documents
  const categories = [
    { value: 'tous', label: 'Toutes les catégories' },
    { value: 'clients', label: 'Documents Clients' },
    { value: 'contrats', label: 'Contrats' },
    { value: 'factures', label: 'Factures' },
    { value: 'administratif', label: 'Administratif' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'reservations', label: 'Réservations' },
    { value: 'fournisseurs', label: 'Fournisseurs' },
    { value: 'finance', label: 'Finance' }
  ];

  // Types de documents
  const types = [
    { value: 'tous', label: 'Tous les types' },
    { value: 'passeport', label: 'Passeports' },
    { value: 'visa', label: 'Visas' },
    { value: 'contrat', label: 'Contrats' },
    { value: 'facture', label: 'Factures' },
    { value: 'recu', label: 'Reçus' },
    { value: 'photo', label: 'Photos' },
    { value: 'certificat', label: 'Certificats' },
    { value: 'itineraire', label: 'Itinéraires' },
    { value: 'marketing', label: 'Marketing' }
  ];

  const handleMenuClick = (event, document) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocument(document);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDocument(null);
    setEditingDocument(null);
  };
  
  // Handle document deletion
  const handleDeleteDocument = (document) => {
    if (!document || !document.id) return;
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      try {
        // Set UI state for immediate feedback
        setSelectedDocument(null);
        setAnchorEl(null);
        
        // Call API to delete document
        deleteResource('document', document.id)
          .then(() => {
            // Trigger data refresh (the hook handles cache invalidation)
            window.dispatchEvent(new CustomEvent('documentsDataUpdate'));
            
            // Show success feedback
            console.log('Document supprimé avec succès:', document.id);
          })
          .catch(error => {
            console.error('Error deleting document:', error);
            // If deletion fails, refresh data to restore correct state
            window.dispatchEvent(new CustomEvent('documentsDataUpdate'));
            
            // Show error feedback
            alert('Une erreur est survenue lors de la suppression du document.');
          });
      } catch (error) {
        console.error('Error in handleDeleteDocument:', error);
      }
    }
  };

  // Pagination for card view
  const paginatedDocuments = filteredDocuments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Handler functions for document actions
  const handleViewDocument = (document) => {
    setSelectedDocument(document);
    setPreviewDialog(true);
  };

  // Open dialog for editing an existing document
  const handleEditDocument = (document) => {
    if (!document) return;
    setEditingDocument(document);
    // Pre-fill form data with existing document values
    setNewDocument({
      nom: document.nom,
      type: document.type,
      categorie: document.categorie,
      description: document.description,
      proprietaire: document.proprietaire,
      dateCreation: document.dateCreation,
      statut: document.statut
    });
    setSelectedFile(null); // Reset any previously selected file
    setOpenDialog(true);
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'validé': case 'signé': case 'payé': case 'actif': return '#059669';
      case 'en_attente': return '#f59e0b';
      case 'archivé': return '#6b7280';
      default: return '#1e40af';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'photo': return <ImageIcon />;
      case 'contrat': case 'certificat': return <AssignmentIcon />;
      case 'facture': case 'recu': return <BusinessIcon />;
      case 'visa': case 'passeport': return <FolderIcon />;
      default: return <DescriptionIcon />;
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target ? event.target.value : event);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
  };

  const handleFileSelect = (event) => {
    console.log('🟢 ============ FILE SELECT TRIGGERED (HTML INPUT) ============');
    console.log('🟢 Event:', event);
    console.log('🟢 Event target:', event.target);
    console.log('🟢 Files:', event.target.files);
    
    const file = event.target.files[0];
    console.log('🟢 Selected file:', file);
    
    if (file) {
      console.log('✅ File selected successfully:', file.name);
      console.log('✅ File size:', file.size);
      console.log('✅ File type:', file.type);
      
      setSelectedFile(file);
      // Auto-fill the document name with the file name
      setNewDocument({
        ...newDocument,
        nom: file.name
      });
      console.log('✅ State updated with selected file');
    } else {
      console.warn('⚠️ No file was selected');
    }
    
    console.log('🟢 ============ END FILE SELECT ============');
  };

  // NEW: Handle file selection via Electron native dialog
  const handleElectronFileSelect = async () => {
    console.log('🟣 ============ ELECTRON NATIVE FILE DIALOG ============');
    
    try {
      if (window.electronAPI && window.electronAPI.openFileDialog) {
        console.log('✅ Electron API available, opening native dialog...');
        
        const result = await window.electronAPI.openFileDialog({
          title: 'Sélectionner un document',
          filters: [
            { name: 'Tous les fichiers', extensions: ['*'] },
            { name: 'Documents', extensions: ['pdf', 'doc', 'docx', 'txt'] },
            { name: 'Images', extensions: ['jpg', 'jpeg', 'png', 'gif'] }
          ]
        });
        
        console.log('🟣 Dialog result:', result);
        
        if (!result.canceled && result.fileName) {
          console.log('✅ File selected via Electron:', result.fileName);
          console.log('✅ File size:', result.fileSize);
          console.log('✅ File path:', result.filePath);
          
          // Create a File-like object for compatibility
          const fileObject = {
            name: result.fileName,
            size: result.fileSize,
            type: result.fileType || 'application/octet-stream',
            path: result.filePath,
            electronFile: true, // Mark as Electron file
            data: result.fileData // Base64 data if available
          };
          
          setSelectedFile(fileObject);
          setNewDocument({
            ...newDocument,
            nom: result.fileName
          });
          
          console.log('✅ State updated with Electron file');
        } else {
          console.log('⚠️ File selection canceled');
        }
      } else {
        console.warn('⚠️ Electron API not available, falling back to HTML input');
        // Fallback to HTML file input for non-Electron environments
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }
    } catch (error) {
      console.error('❌ Error opening Electron file dialog:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Falling back to HTML input');
      
      // Fallback to HTML file input
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
    
    console.log('🟣 ============ END ELECTRON FILE DIALOG ============');
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      setSelectedFile(file);
      // Auto-fill the document name with the file name
      setNewDocument({
        ...newDocument,
        nom: file.name
      });
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    // If category changes, reset the proprietaire field
    if (name === 'categorie') {
      setNewDocument({
        ...newDocument,
        [name]: value,
        proprietaire: ''  // Reset proprietaire when category changes
      });
    } else {
      setNewDocument({
        ...newDocument,
        [name]: value
      });
    }
  };

  const handleUpload = async () => {
    // Basic validation
    if (!newDocument.nom || !newDocument.type || !newDocument.categorie) {
      alert('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 300);

    try {
      let result;
      if (editingDocument) {
        // Update existing document
        result = await updateResource('document', editingDocument.id, {
          ...newDocument,
          // Only include file data if the user selected a new file while editing
          ...(selectedFile && { nomFichier: selectedFile.name, taille: selectedFile.size, format: selectedFile.type })
        });
      } else {
        // Upload new document
        if (!selectedFile) {
          alert('Veuillez sélectionner un fichier à télécharger.');
          setIsUploading(false);
          return;
        }
        result = await uploadDocument(newDocument, selectedFile);
      }
      
      if (result.success) {
        // Reset form
        setSelectedFile(null);
        setNewDocument({
          nom: '',
          type: '',
          categorie: '',
          description: '',
          proprietaire: '',
          dateCreation: new Date().toISOString().split('T')[0],
          statut: 'actif'
        });
        setOpenDialog(false);
        setEditingDocument(null);
      } else {
        alert(`Erreur lors du téléchargement: ${result.error}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert("Une erreur est survenue lors de l'enregistrement du document.");
    } finally {
      clearInterval(interval);
      setUploadProgress(0);
      setIsUploading(false);
    }
  };

  // Handle document preview
  const handlePreview = (document) => {
    setSelectedDocument(document);
    setPreviewDialog(true);
    setAnchorEl(null); // Close the menu if open
  };

  // Handle document download
  const handleDownload = (doc) => {
    setDownloadingDoc(doc.id);
    
    setTimeout(() => {
      try {
        // Check if we have file data from an upload
        if (doc.fileData && doc.fileData.data) {
          // Create a download link using the stored base64 data
          const link = globalDocument.createElement('a');
          link.href = doc.fileData.data;
          link.download = doc.nom;
          globalDocument.body.appendChild(link);
          link.click();
          globalDocument.body.removeChild(link);
        } else {
          // Fallback to simulated download for documents without file data
          const link = globalDocument.createElement('a');
          
          // For images, create a data URL
          if (doc.nom && doc.nom.match(/\.(jpeg|jpg|gif|png)$/i)) {
            const canvas = globalDocument.createElement('canvas');
            canvas.width = 800;
            canvas.height = 600;
            const ctx = canvas.getContext('2d');
            
            // Fill with a gradient to simulate an image
            const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
            gradient.addColorStop(0, '#1e40af');
            gradient.addColorStop(1, '#7c3aed');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Add some text
            ctx.fillStyle = 'white';
            ctx.font = '30px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('Document Simulé', canvas.width/2, canvas.height/2);
            ctx.fillText(doc.nom, canvas.width/2, canvas.height/2 + 40);
            
            link.href = canvas.toDataURL('image/png');
          } else if (doc.nom && doc.nom.match(/\.(pdf)$/i)) {
            // For PDFs, create a simple blob
            const blob = new Blob(['Contenu PDF simulé pour ' + doc.nom], { type: 'application/pdf' });
            link.href = URL.createObjectURL(blob);
          } else {
            // For other files, create a text file
            const blob = new Blob(['Contenu simulé pour ' + doc.nom], { type: 'text/plain' });
            link.href = URL.createObjectURL(blob);
          }
          
          link.download = doc.nom;
          globalDocument.body.appendChild(link);
          link.click();
          globalDocument.body.removeChild(link);
        }
      } catch (error) {
        console.error('Download error:', error);
        alert('Une erreur est survenue lors du téléchargement du document.');
      } finally {
        setDownloadingDoc(null);
        setAnchorEl(null); // Close the menu if open
      }
    }, 500); // Reduced delay for better user experience
  };

  // Get document preview content based on type
  const getDocumentPreview = (document) => {
    if (!document) return null;
    
    // Check if we have file data from an upload
    if (document.fileData && document.fileData.data) {
      if (document.fileData.type.startsWith('image/')) {
        // For uploaded images, show the actual image
        return (
          <Box sx={{ 
            width: '100%', 
            height: 'auto', 
            maxHeight: 500,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 2,
            mb: 2,
            overflow: 'hidden'
          }}>
            <img 
              src={document.fileData.data} 
              alt={document.nom}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%',
                objectFit: 'contain'
              }} 
            />
          </Box>
        );
      } else if (document.fileData.type === 'application/pdf') {
        // For PDFs, show a red div with the document name and an iframe if possible
        return (
          <Box sx={{ 
            width: '100%', 
            height: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 2,
            mb: 2,
            overflow: 'hidden',
            border: '1px solid #e2e8f0'
          }}>
            <Box sx={{
              width: '100%',
              height: 80,
              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white'
            }}>
              <PdfIcon sx={{ fontSize: 40, mr: 2 }} />
              <Typography variant="h6">
                Document PDF: {document.nom}
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 'calc(100% - 80px)', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <Button 
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => document && handleDownload(document)}
                sx={{
                  bgcolor: '#059669',
                  '&:hover': {
                    bgcolor: '#047857'
                  },
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  mb: 2
                }}
              >
                Télécharger le PDF
              </Button>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                L'aperçu PDF n'est pas disponible. Veuillez télécharger le document pour le consulter.
              </Typography>
            </Box>
          </Box>
        );
      } else {
        // For other files, show a blue div with the document name
        return (
          <Box sx={{ 
            width: '100%', 
            height: 400, 
            background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            borderRadius: 2,
            mb: 2
          }}>
            <FileIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Document
            </Typography>
            <Typography variant="body1">
              {document.nom}
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
              Type: {document.fileData.type || 'Inconnu'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Taille: {(document.fileData.size / (1024 * 1024)).toFixed(2)} MB
            </Typography>
            <Button 
              variant="contained"
              startIcon={<DownloadIcon />}
                              onClick={() => document && handleDownload(document)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                },
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                mt: 3
              }}
            >
              Télécharger
            </Button>
          </Box>
        );
      }
    } else {
      // Fallback for documents without file data
      if (document.nom && document.nom.match(/\.(jpeg|jpg|gif|png)$/i)) {
        // For images, try to display the actual image
        const imageUrl = document.contenu || document.url || `/api/documents/${document.id}/content`;
        return (
          <Box sx={{ 
            width: '100%', 
            height: 'auto', 
            maxHeight: 500,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 2,
            mb: 2,
            overflow: 'hidden'
          }}>
            <img 
              src={imageUrl} 
              alt={document.nom}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%',
                objectFit: 'contain'
              }} 
              onError={(e) => {
                // If image fails to load, show fallback
                e.target.style.display = 'none';
                const fallbackEl = globalDocument.createElement('div');
                fallbackEl.innerHTML = `
                  <div style="
                    width: 100%;
                    height: 400px;
                    background: linear-gradient(135deg, #1e40af 0%, #7c3aed 100%);
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    color: white;
                    border-radius: 8px;
                  ">
                    <div style="font-size: 80px; margin-bottom: 16px; opacity: 0.8;">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-6c.83 0 1.5-.67 1.5-1.5S12.83 11 12 11s-1.5.67-1.5 1.5.67 1.5 1.5 1.5zm0-9C9.24 5 7 7.24 7 10h2c0-1.66 1.34-3 3-3s3 1.34 3 3h2c0-2.76-2.24-5-5-5z"/>
                      </svg>
                    </div>
                    <div style="font-size: 24px; margin-bottom: 8px;">
                      Aperçu non disponible
                    </div>
                    <div>${document.nom}</div>
                  </div>
                `;
                e.target.parentNode.appendChild(fallbackEl.firstElementChild);
              }}
            />
          </Box>
        );
      } else if (document.nom && document.nom.match(/\.(pdf)$/i)) {
        // For PDFs, show a red div with the document name
        return (
          <Box sx={{ 
            width: '100%', 
            height: 400, 
            background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            borderRadius: 2,
            mb: 2
          }}>
            <PdfIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Document PDF
            </Typography>
            <Typography variant="body1">
              {document.nom}
            </Typography>
          </Box>
        );
      } else {
        // For other files, show a blue div with the document name
        return (
          <Box sx={{ 
            width: '100%', 
            height: 400, 
            background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            borderRadius: 2,
            mb: 2
          }}>
            <FileIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
            <Typography variant="h5" sx={{ mb: 1 }}>
              Document
            </Typography>
            <Typography variant="body1">
              {document.nom}
            </Typography>
          </Box>
        );
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 0, md: 3 } }} className="space-y-6">
      {/* Header Section */}
      <Box sx={{ 
        mb: { xs: 2, md: 4 },
        p: { xs: 2, md: 0 }
      }} className="flex justify-between items-center">
        <div>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            color: 'white',
            mb: 1.5,
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
            letterSpacing: '-0.01em'
          }}>
            Centre de Documents
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'rgba(255,255,255,0.6)',
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
          }}>
            Gérez tous les documents de votre agence de voyage
          </Typography>
        </div>
      </Box>
      
      {/* Quick Actions Section */}
      {showQuickActions && (
      <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <RocketLaunch sx={{ mr: 1.5, color: '#f59e0b', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Actions Rapides
          </Typography>
        </Box>
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={() => setOpenDialog(true)}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-800">
                  <CloudUploadIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Nouveau Document</h3>
              <p className="text-white/60 text-sm">Ajouter un document au système</p>
            </motion.div>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={() => document.getElementById('document-search').focus()}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-green-800">
                  <FilterListIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Filtrer Documents</h3>
              <p className="text-white/60 text-sm">Rechercher des documents</p>
            </motion.div>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={() => setSelectedCategory('clients')}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-500 to-amber-700">
                  <AssignmentIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Documents Clients</h3>
              <p className="text-white/60 text-sm">Voir les documents clients</p>
            </motion.div>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={() => {
                setSelectedCategory('contrats');
                setSelectedType('contrat');
              }}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-600 to-purple-800">
                  <BusinessIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Contrats</h3>
              <p className="text-white/60 text-sm">Gérer les contrats</p>
            </motion.div>
          </Grid>
        </Grid>
      </Box>)}

      {/* Key Metrics Section */}
      <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoAwesome sx={{ mr: 1.5, color: '#10b981', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Métriques en Temps Réel
          </Typography>
        </Box>
        
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {statsCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                    <p className="text-2xl font-bold text-white mt-2">
                      {loading ? <CircularProgress size={24} /> : stat.value.toLocaleString()}
                    </p>
                    <p className="text-white/60 text-sm mt-1">
                      {stat.title.includes('Clients') ? 'Documents personnels' : 
                       stat.title.includes('Contrats') ? 'Contrats actifs' : 
                       stat.title.includes('Attente') ? 'Documents en attente de validation' :
                       'Documents stockés'}
                    </p>
                    {stat.change && (
                      <span className={`inline-flex items-center px-2 py-0.5 mt-2 rounded-full text-xs font-medium ${
                        stat.change.startsWith('+') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {stat.change}
                      </span>
                    )}
                  </div>
                  <div className={`p-3 rounded-2xl bg-gradient-to-r ${
                    index === 0 ? 'from-blue-600 to-indigo-600' : 
                    index === 1 ? 'from-green-600 to-green-800' : 
                    index === 2 ? 'from-amber-500 to-amber-700' : 
                    'from-purple-600 to-purple-800'
                  }`}>
                    {stat.icon}
                  </div>
                </div>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

            {/* Recherche de Documents - style matched to Fournisseurs page */}
      <Box sx={{ mb: 3, mx: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SearchIcon sx={{ mr: 1.5, color: '#f59e0b', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Recherche de Documents
          </Typography>
        </Box>
        
        <FilterBar
          searchValue={{
            value: searchTerm,
            placeholder: "Rechercher un document..."
          }}
          onSearchChange={handleSearch}
          filters={[
            {
              label: "Catégorie",
              value: selectedCategory,
              onChange: handleCategoryChange,
              options: categories
            },
            {
              label: "Type",
              value: selectedType,
              onChange: handleTypeChange,
              options: types
            }
          ]}
          primaryButton={{
            label: "Rechercher",
            icon: <SearchIcon />,
            onClick: () => {/* Search is already reactive */}
          }}
          secondaryButton={{
            label: "Filtrer",
            icon: <FilterAltIcon />,
            onClick: () => {/* Filtering is handled by select controls */}
          }}
        />
      </Box>

      {/* Liste des Documents */}
      <Box sx={{ px: { xs: 2, md: 0 }, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <DescriptionIcon sx={{ mr: 1.5, color: '#8b5cf6', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Liste des Documents
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* View Mode Toggle */}
            <Box sx={{ display: 'flex', bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, p: 0.5 }}>
            <Button
                onClick={() => setViewMode('table')}
              sx={{ 
                  minWidth: 40,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  color: viewMode === 'table' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                  bgcolor: viewMode === 'table' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                '&:hover': {
                    bgcolor: viewMode === 'table' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                  },
              }}
            >
                <TableViewIcon />
            </Button>
              <Button
                onClick={() => setViewMode('card')}
                sx={{
                  minWidth: 40,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  color: viewMode === 'card' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                  bgcolor: viewMode === 'card' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                  '&:hover': {
                    bgcolor: viewMode === 'card' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <CardViewIcon />
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                },
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
              }}
            >
              Nouveau Document
            </Button>
          </Box>
        </Box>
      </Box>
      
      {/* Documents Display - Table or Card View */}
      <Grid item xs={12}>
        <>
          {/* Table View */}
          {viewMode === 'table' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden relative mx-2 md:mx-0 mb-4">
            {loading && (
              <LinearProgress 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  height: 3, 
                  borderTopLeftRadius: 3,
                  borderTopRightRadius: 3,
                  zIndex: 1
                }} 
              />
            )}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-4 text-left text-white font-semibold">Document</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Type</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Propriétaire</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Taille</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Date</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <CircularProgress sx={{ color: '#8b5cf6' }} />
                    <p className="mt-4 text-white/70">Chargement des documents...</p>
                  </div>
                </td>
              </tr>
            ) : filteredDocuments.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="p-3 rounded-full bg-white/10 mb-3">
                      <DescriptionIcon className="h-8 w-8 text-white/50" />
                    </div>
                    <p className="text-white/70">Aucun document trouvé</p>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenDialog(true)}
                      sx={{ 
                        mt: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                        },
                      }}
                    >
                      Ajouter un document
                    </Button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredDocuments
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((doc) => (
                <motion.tr
                  key={doc.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-white/5 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-xl ${doc.statut === 'actif' ? 'bg-green-500/20' : doc.statut === 'en_attente' ? 'bg-amber-500/20' : 'bg-red-500/20'}`}>
                        {getTypeIcon(doc.type)}
                      </div>
                      <div>
                        <p className="text-white font-medium">{doc.nom}</p>
                        <p className="text-white/60 text-sm">{doc.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-300">
                      {doc.type.charAt(0).toUpperCase() + doc.type.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-white font-medium">
                      {doc.proprietaire || 'Non spécifié'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-white font-medium">
                      {doc.taille || 'Non spécifié'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-white font-medium">
                      {doc.dateCreation ? new Date(doc.dateCreation).toLocaleDateString('fr-FR') : 'Invalid Date'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      doc.statut === 'actif' 
                        ? 'bg-green-500/20 text-green-400' 
                        : doc.statut === 'en_attente'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {doc.statut === 'actif' ? 'Actif' : doc.statut === 'en_attente' ? 'En Attente' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePreview(doc)}
                        className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                        title="Voir"
                      >
                        <VisibilityIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditDocument(doc)}
                        className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                        title="Modifier"
                      >
                        <EditIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc)}
                        className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                        title="Supprimer"
                      >
                        <DeleteIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
              <div className="text-sm text-white/60">
                Affichage de {(page * rowsPerPage) + 1} à {Math.min((page + 1) * rowsPerPage, filteredDocuments.length)} sur {filteredDocuments.length} documents
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setPage(Math.min(Math.ceil(filteredDocuments.length / rowsPerPage) - 1, page + 1))}
                  disabled={page >= Math.ceil(filteredDocuments.length / rowsPerPage) - 1}
                  className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            </div>
              </div>
            </motion.div>
          )}

          {/* Card View */}
          {viewMode === 'card' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="px-2 md:px-0 mb-4"
            >
              {loading ? (
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-12 text-center">
                  <CircularProgress sx={{ color: '#8b5cf6' }} />
                  <p className="mt-4 text-white/70">Chargement des documents...</p>
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-12 text-center">
                  <div className="p-3 rounded-full bg-white/10 mb-3 mx-auto w-fit">
                    <DescriptionIcon className="h-8 w-8 text-white/50" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Aucun document trouvé</h3>
                  <p className="text-white/60 mb-6">Aucun document ne correspond à vos critères de recherche.</p>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                      },
                    }}
                  >
                    Ajouter un document
                  </Button>
                </div>
              ) : (
                <>
                  <Grid container spacing={3}>
                    {paginatedDocuments.map((doc) => {
                      const fileExtension = doc.nom?.split('.').pop()?.toLowerCase();
                      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(fileExtension);
                      const isPdf = fileExtension === 'pdf';
                      
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                            className="h-full"
                          >
                            <Card
                              sx={{
                                height: '100%',
                                background: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(20px)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: 3,
                                color: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                '&:hover': {
                                  background: 'rgba(255, 255, 255, 0.15)',
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                },
                              }}
                              onClick={() => handleViewDocument(doc)}
                            >
                              <CardContent sx={{ p: 3, pb: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                {/* Document Icon and Type */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Avatar 
                                    sx={{ 
                                      bgcolor: isPdf ? 'rgb(220, 38, 38)' : isImage ? 'rgb(34, 197, 94)' : 'rgb(59, 130, 246)',
                                      width: 48,
                                      height: 48,
                                      mr: 2
                                    }}
                                  >
                                    {isPdf ? <PdfIcon /> : isImage ? <ImageIcon /> : <FileIcon />}
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ 
                                      color: 'white',
                                      fontWeight: 600,
                                      fontSize: '0.95rem',
                                      mb: 0.5,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {doc.nom}
                                    </Typography>
                    <Chip
                                      label={doc.type}
                      size="small"
                      sx={{
                                        backgroundColor: 'rgba(139, 92, 246, 0.2)',
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                  </Box>
                                </Box>

                                {/* Document Details */}
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography variant="body2" sx={{ 
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    fontSize: '0.875rem',
                                    mb: 1
                                  }}>
                                    Propriétaire: {doc.proprietaire || 'Non défini'}
                                  </Typography>
                                  <Typography variant="body2" sx={{ 
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontSize: '0.75rem'
                                  }}>
                                    {doc.taille && (
                                      <>Taille: {(doc.taille / 1024).toFixed(1)} KB • </>
                                    )}
                                    {doc.dateCreation && new Date(doc.dateCreation).toLocaleDateString('fr-FR')}
                                  </Typography>
                                </Box>

                                {/* Status */}
                                <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Chip
                                    label={doc.statut}
                      size="small"
                      sx={{
                                      backgroundColor: doc.statut === 'actif' ? 'rgba(34, 197, 94, 0.8)' : 'rgba(245, 158, 11, 0.8)',
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ 
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    fontSize: '0.75rem'
                                  }}>
                                    {doc.categorie}
                                  </Typography>
                                </Box>
                              </CardContent>

                              {/* Actions */}
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
                                <Tooltip title="Voir le document">
                        <IconButton 
                          size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewDocument(doc);
                                    }}
                                    sx={{
                                      color: 'rgb(34, 197, 94)',
                                      bgcolor: 'rgba(34, 197, 94, 0.1)',
                                      '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)' },
                                      width: 36,
                                      height: 36,
                                    }}
                                  >
                                    <VisibilityIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Modifier">
                        <IconButton 
                          size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditDocument(doc);
                                    }}
                                    sx={{
                                      color: 'rgb(245, 158, 11)',
                                      bgcolor: 'rgba(245, 158, 11, 0.1)',
                                      '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.2)' },
                                      width: 36,
                                      height: 36,
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteDocument(doc);
                                    }}
                                    sx={{
                                      color: 'rgb(239, 68, 68)',
                                      bgcolor: 'rgba(239, 68, 68, 0.1)',
                                      '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                                      width: 36,
                                      height: 36,
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                            </Card>
                          </motion.div>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Pagination for card view */}
                  <Box sx={{ 
                    mt: 4, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      Affichage de {(page * rowsPerPage) + 1} à {Math.min((page + 1) * rowsPerPage, filteredDocuments.length)} sur {filteredDocuments.length} documents
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setPage(Math.max(0, page - 1))}
                        disabled={page === 0}
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        Précédent
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setPage(Math.min(Math.ceil(filteredDocuments.length / rowsPerPage) - 1, page + 1))}
                        disabled={page >= Math.ceil(filteredDocuments.length / rowsPerPage) - 1}
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          },
                        }}
                      >
                        Suivant
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
        </motion.div>
          )}
        </>
      </Grid>

      {/* Menu d'actions sur document */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: '12px',
            minWidth: 180
          }
        }}
      >
        <MenuItem onClick={() => selectedDocument && handlePreview(selectedDocument)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1, color: '#1e40af' }} />
          Voir le document
        </MenuItem>
        <MenuItem onClick={() => selectedDocument && handleDownload(selectedDocument)}>
          <DownloadIcon fontSize="small" sx={{ mr: 1, color: '#059669' }} />
          Télécharger
        </MenuItem>
        <MenuItem onClick={() => selectedDocument && handleDeleteDocument(selectedDocument)}>
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: '#dc2626' }} />
          Supprimer
        </MenuItem>
      </Menu>

      {/* Document Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          pb: 2
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
              {selectedDocument?.nom}
          </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {selectedDocument?.description}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setPreviewDialog(false)} 
            size="small"
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {selectedDocument && getDocumentPreview(selectedDocument)}
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                Type
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                {selectedDocument?.type.charAt(0).toUpperCase() + selectedDocument?.type.slice(1)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                Taille
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                {selectedDocument?.taille}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                Date de création
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                {selectedDocument && new Date(selectedDocument.dateCreation).toLocaleDateString('fr-FR')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                Propriétaire
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                {selectedDocument?.proprietaire}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
          <Button 
            onClick={() => setPreviewDialog(false)}
            variant="outlined"
                  sx={{
              borderRadius: 2,
              borderColor: 'rgba(255, 255, 255, 0.2)',
              color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Fermer
          </Button>
          <Button 
            variant="contained"
            onClick={() => selectedDocument && handleDownload(selectedDocument)}
            disabled={downloadingDoc === selectedDocument?.id}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
              }
            }}
          >
            {downloadingDoc === selectedDocument?.id ? 'Téléchargement...' : 'Télécharger'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'ajout de document */}
      <Dialog 
        open={openDialog} 
        onClose={() => !isUploading && setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            maxWidth: 600,
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: 'white', pb: 1 }}>
          {editingDocument ? 'Modifier le document' : 'Ajouter un nouveau document'}
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom du document"
                variant="outlined"
                name="nom"
                value={newDocument.nom}
                onChange={handleInputChange}
                required
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
              </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Type</InputLabel>
                <Select
                  label="Type"
                  name="type"
                  value={newDocument.type}
                  onChange={handleInputChange}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  {types.filter(t => t.value !== 'tous').map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Catégorie</InputLabel>
                <Select
                  label="Catégorie"
                  name="categorie"
                  value={newDocument.categorie}
                  onChange={handleInputChange}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  {categories.filter(c => c.value !== 'tous').map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}>Statut</InputLabel>
                  <Select
                    label="Statut"
                    name="statut"
                    value={newDocument.statut}
                    onChange={handleInputChange}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: 2,
                          mt: 1,
                          '& .MuiMenuItem-root': {
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.3)'
                              }
                            }
                          }
                        }
                      }
                    }}
                    sx={{ 
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiSelect-icon': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      }
                    }}
                  >
                    <MenuItem value="actif">Actif</MenuItem>
                    <MenuItem value="validé">Validé</MenuItem>
                    <MenuItem value="en_attente">En attente</MenuItem>
                    <MenuItem value="archivé">Archivé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                {newDocument.categorie === 'clients' ? (
                  <Autocomplete
                    fullWidth
                    options={clients || []}
                    getOptionLabel={(client) => `${client.prenom} ${client.nom}`}
                    loading={clientsLoading}
                    value={clients?.find(c => `${c.prenom} ${c.nom}` === newDocument.proprietaire) || null}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        setNewDocument({
                          ...newDocument,
                          proprietaire: `${newValue.prenom} ${newValue.nom}`
                        });
                      } else {
                        setNewDocument({
                          ...newDocument,
                          proprietaire: ''
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Propriétaire"
                        required
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white'
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.2)'
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)'
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: 'rgba(255, 255, 255, 0.5)',
                            opacity: 1
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {clientsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                ) : newDocument.categorie === 'fournisseurs' ? (
                  <Autocomplete
                    fullWidth
                    options={suppliers || []}
                    getOptionLabel={(supplier) => supplier.nom}
                    loading={suppliersLoading}
                    value={suppliers?.find(s => s.nom === newDocument.proprietaire) || null}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        setNewDocument({
                          ...newDocument,
                          proprietaire: newValue.nom
                        });
                      } else {
                        setNewDocument({
                          ...newDocument,
                          proprietaire: ''
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Propriétaire"
                        required
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white'
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.2)'
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)'
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: 'rgba(255, 255, 255, 0.5)',
                            opacity: 1
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {suppliersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Propriétaire"
                    variant="outlined"
                    name="proprietaire"
                    value={newDocument.proprietaire}
                    onChange={handleInputChange}
                    required
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1
                      }
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  variant="outlined"
                  name="dateCreation"
                  value={newDocument.dateCreation}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      opacity: 1
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  variant="outlined"
                  multiline
                  rows={3}
                  name="description"
                  value={newDocument.description}
                  onChange={handleInputChange}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      opacity: 1
                    }
                  }}
                />
              </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: '2px dashed rgba(255, 255, 255, 0.3)',
                  borderRadius: '12px',
                  p: 3,
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {selectedFile ? (
                  <>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#10b981', mb: 1 }}>
                      Fichier sélectionné: {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                  </>
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.7)', mb: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'white', mb: 0.5 }}>
                      Déposez votre fichier ici ou
                    </Typography>
                    <Button 
                      variant="outlined"
                      onClick={handleElectronFileSelect}
                      sx={{ 
                        borderRadius: '8px',
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      Parcourir
                    </Button>
                  </>
                )}
                <input
                  type="file"
                  id="document-file-input"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="*/*"
                />
              </Box>
            </Grid>
            {isUploading && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%', mt: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#7c3aed'
                      }
                    }} 
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: '#64748b', textAlign: 'center' }}>
                    {uploadProgress}% Téléchargé
                  </Typography>
          </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            disabled={isUploading}
            sx={{ borderRadius: 2, color: 'white' }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={isUploading || !selectedFile}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
              color: 'white !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                color: 'white !important',
              },
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.6) !important',
              }
            }}
          >
            {isUploading ? (editingDocument ? 'Enregistrement...' : 'Téléchargement...') : (editingDocument ? 'Enregistrer' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bouton d'action flottant */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%)'
          }
        }}
        onClick={() => setOpenDialog(true)}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default Documents; 